import { Controller } from "@hotwired/stimulus"
import { fetch as turboFetch } from "@hotwired/turbo"

// FD-82/83/91: interactive Gantt timeline.
//  - Drag a bar body to move it (shifts both start & target by whole days).
//  - Drag either edge handle to resize (changes only start or target).
//  - On drop/resize-end, PATCH the item's dates as JSON to `reschedule`,
//    mirroring the kanban/calendar pattern (no re-render; JS moves the DOM).
//  - Draws SVG dependency connectors (blocked-by/blocking) that reposition
//    live while a bar is dragged, and highlights the critical-path chain.
export default class extends Controller {
  static targets = ["canvas", "bar", "connectors", "edge"]
  static values = {
    dayWidth: Number,
    rowHeight: Number,
    rangeStart: String,
    days: Number
  }

  connect() {
    this.onPointerMove = this.pointerMove.bind(this)
    this.onPointerUp = this.pointerUp.bind(this)
    this.onPointerCancel = this.pointerCancel.bind(this)
    this.drag = null
    // Delegated from the container, like kanban/calendar: a broadcast morph that grows the
    // row count appends bars WITHOUT reconnecting this controller, so a listener wired per
    // bar at connect() left every later row permanently undraggable for the session.
    this.onPointerDown = (event) => {
      const bar = event.target.closest("[data-gantt-target~='bar']")
      if (bar) this.pointerDown(event, bar)
    }
    this.element.addEventListener("pointerdown", this.onPointerDown)
    this.drawConnectors()
    this.resizeObserver = new ResizeObserver(() => this.drawConnectors())
    if (this.hasCanvasTarget) this.resizeObserver.observe(this.canvasTarget)
    // Same morph, the connector layer: the server renders an EMPTY <svg>, so idiomorph
    // strips the paths we drew — and morph-added rows bring edges nobody has drawn yet.
    // Redraw once the morph has landed (geometry is final by then).
    this.onDocMorph = () => this.drawConnectors()
    document.addEventListener("turbo:morph", this.onDocMorph)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onPointerDown)
    document.removeEventListener("turbo:morph", this.onDocMorph)
    document.removeEventListener("pointermove", this.onPointerMove)
    document.removeEventListener("pointerup", this.onPointerUp)
    document.removeEventListener("pointercancel", this.onPointerCancel)
    this.resizeObserver?.disconnect()
  }

  pointerDown(event, bar) {
    if (event.button !== 0) return
    const handle = event.target.closest("[data-gantt-handle]")
    const mode = handle ? `resize-${handle.dataset.ganttHandle}` : "move"
    this.drag = {
      bar,
      mode,
      pointerId: event.pointerId,
      startX: event.clientX,
      originLeft: bar.offsetLeft,
      originWidth: bar.offsetWidth,
      moved: false
    }
    bar.classList.add("cursor-grabbing", "z-10", "shadow-lg")
    document.addEventListener("pointermove", this.onPointerMove)
    document.addEventListener("pointerup", this.onPointerUp)
    document.addEventListener("pointercancel", this.onPointerCancel)
    event.preventDefault()
  }

  pointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    const day = this.dayWidthValue
    const rawDelta = event.clientX - this.drag.startX
    const snappedDays = Math.round(rawDelta / day)
    if (snappedDays !== 0) this.drag.moved = true
    const delta = snappedDays * day
    const bar = this.drag.bar

    if (this.drag.mode === "move") {
      bar.style.left = `${Math.max(0, this.drag.originLeft + delta)}px`
    } else if (this.drag.mode === "resize-end") {
      bar.style.width = `${Math.max(day, this.drag.originWidth + delta)}px`
    } else if (this.drag.mode === "resize-start") {
      const newLeft = Math.min(
        this.drag.originLeft + delta,
        this.drag.originLeft + this.drag.originWidth - day
      )
      const clampedLeft = Math.max(0, newLeft)
      bar.style.left = `${clampedLeft}px`
      bar.style.width = `${this.drag.originLeft + this.drag.originWidth - clampedLeft}px`
    }
    this.placeLabel(bar)
    this.drawConnectors()
  }

  async pointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    const drag = this.drag
    this.drag = null
    const bar = drag.bar
    bar.classList.remove("cursor-grabbing", "z-10", "shadow-lg")
    document.removeEventListener("pointermove", this.onPointerMove)
    document.removeEventListener("pointerup", this.onPointerUp)
    document.removeEventListener("pointercancel", this.onPointerCancel)

    if (!drag.moved) return

    const startDays = Math.round(bar.offsetLeft / this.dayWidthValue)
    const spanDays = Math.max(1, Math.round(bar.offsetWidth / this.dayWidthValue))
    const newStart = this.dateAtOffset(startDays)
    const newTarget = this.dateAtOffset(startDays + spanDays - 1)

    const body = {}
    if (drag.mode === "move" || drag.mode === "resize-start") body.start_date = newStart
    if (drag.mode === "move" || drag.mode === "resize-end") body.target_date = newTarget

    bar.dataset.startDate = newStart
    bar.dataset.targetDate = newTarget

    try {
      // FD-489: turboFetch sends X-Turbo-Request-Id so the actor's own refresh
      // broadcast is deduped client-side (the drag already moved the DOM).
      const response = await turboFetch(`${bar.dataset.itemUrl}/reschedule`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
          Accept: "application/json"
        },
        body: JSON.stringify(body)
      })
      if (!response.ok) this.revert(drag)
    } catch (_e) {
      this.revert(drag)
    }
    this.drawConnectors()
  }

  // A touch drag that turns into a scroll fires pointercancel and never a pointerup, which
  // would otherwise leave this.drag armed — the next tap then reads a stale startX and can
  // PATCH /reschedule with teleported dates. Abort cleanly: snap the bar back and reset state.
  pointerCancel(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return
    const drag = this.drag
    this.drag = null
    drag.bar.classList.remove("cursor-grabbing", "z-10", "shadow-lg")
    this.revert(drag)
    document.removeEventListener("pointermove", this.onPointerMove)
    document.removeEventListener("pointerup", this.onPointerUp)
    document.removeEventListener("pointercancel", this.onPointerCancel)
    this.drawConnectors()
  }

  revert(drag) {
    drag.bar.style.left = `${drag.originLeft}px`
    drag.bar.style.width = `${drag.originWidth}px`
    this.placeLabel(drag.bar)
  }

  // FD-925: a team Gantt shows a bar's people just past its end (_layout_gantt places it
  // 6px out). The label shares the bar's row and its spotlight marker; keep it at the bar's
  // end as the bar moves. A project's Gantt has no label.
  placeLabel(bar) {
    const label = bar.parentElement?.querySelector(
      "[data-gantt-spotlight-target~='item']:not([data-gantt-target~='bar'])"
    )
    if (label) label.style.left = `${bar.offsetLeft + bar.offsetWidth + 6}px`
  }

  // Convert a whole-day offset from range_start into an ISO date string.
  // FD-474: read back the LOCAL Y/M/D — the base is local midnight, so
  // toISOString() (UTC) would shift the date a day earlier for any UTC-positive
  // viewer, walking a dragged bar's dates backward on every reschedule.
  dateAtOffset(offsetDays) {
    const base = new Date(`${this.rangeStartValue}T00:00:00`)
    base.setDate(base.getDate() + offsetDays)
    const y = base.getFullYear()
    const m = String(base.getMonth() + 1).padStart(2, "0")
    const d = String(base.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  barFor(id) {
    return this.barTargets.find((b) => b.dataset.ganttItemId === String(id))
  }

  // Geometry of a bar relative to the connector SVG (which starts below the
  // header row). Returns the left edge, right edge and vertical center.
  geom(bar) {
    const rowH = this.rowHeightValue
    const rowIndex = this.barTargets.indexOf(bar)
    const top = rowIndex * rowH + rowH / 2
    return { left: bar.offsetLeft, right: bar.offsetLeft + bar.offsetWidth, y: top }
  }

  drawConnectors() {
    if (!this.hasConnectorsTarget) return
    const svg = this.connectorsTarget
    const ns = "http://www.w3.org/2000/svg"
    svg.innerHTML = ""

    this.edgeTargets.forEach((edge) => {
      const source = this.barFor(edge.dataset.ganttEdgeSource)
      const target = this.barFor(edge.dataset.ganttEdgeTarget)
      if (!source || !target) return
      const s = this.geom(source)
      const t = this.geom(target)
      // Arrow leaves the blocker's right edge, enters the blocked item's left.
      const x1 = s.right
      const y1 = s.y
      const x2 = t.left
      const y2 = t.y
      const critical = edge.dataset.ganttEdgeCritical === "true"
      const stroke = critical
        ? "var(--color-warning-tone, #f59e0b)"
        : "color-mix(in srgb, currentColor 45%, transparent)"
      const mid = x1 + Math.max(12, (x2 - x1) / 2)

      const path = document.createElementNS(ns, "path")
      path.setAttribute("d", `M ${x1} ${y1} H ${mid} V ${y2} H ${x2}`)
      path.setAttribute("fill", "none")
      path.setAttribute("stroke", stroke)
      path.setAttribute("stroke-width", critical ? "2" : "1.5")
      svg.appendChild(path)

      // Arrowhead at the target.
      const head = document.createElementNS(ns, "path")
      head.setAttribute("d", `M ${x2} ${y2} l -5 -3 l 0 6 z`)
      head.setAttribute("fill", stroke)
      svg.appendChild(head)
    })
  }
}
